import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, IconButton, useTheme, Button, CircularProgress, Accordion, AccordionSummary, AccordionDetails, Paper, FormControl, Select, MenuItem } from "@mui/material";
import { getAuthToken } from "../../utils/getAuthToken";
import { checkSubscriptionActive } from "../../utils/subscriptionError";
import { toast } from "react-hot-toast";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from "@mui/icons-material/Close";
import CategoryIcon from '@mui/icons-material/Category';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import SettingsIcon from '@mui/icons-material/Settings';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useDispatch, useSelector } from "react-redux";
import { fetchTriggers, addNodeToBranch, updateNodeInBranch, addBranch, setNodeNextBranches } from '../../redux/Slice/Workflow/workflowSlice';
import ReactTimezoneSelect from 'react-timezone-select';
import axios from 'axios';
import OfficeTimeFlowConfig from './OfficeTimeFlowConfig';
import SendTemplateTriggerConfig from './SendTemplateTriggerConfig';
import { buildTimezoneSelectStyles } from './timezoneSelectStyles';
import { isConsoleVerbose } from "../../utils/consoleGate";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

const CATEGORY_ICONS = {
  "Message": <MessageIcon sx={{ mr: 1, color: 'primary.main' }} />,
  "Contact": <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />,
  "Event": <EventIcon sx={{ mr: 1, color: 'primary.main' }} />,
  "Settings": <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />,
  // Add more mappings as needed
};

// Subtitle shown under the category heading — only Shopify has one for now
// (matches the reference layout); other categories are left as before.
const CATEGORY_DESCRIPTIONS = {
  "Shopify": "Triggers based on your Shopify store's activity",
};

// Display order for the Shopify category only, matching the reference image.
// findAllTriggers() (server) has no explicit sort — Mongo doesn't guarantee
// query result order without one, and relying on insertion order turned out
// not to hold in practice. Fixed here instead of adding a DB-level sort, so
// this can't affect any other category's (Message/Contact/Nil) existing
// order at all.
// Display order for the top-level category accordions themselves — same root cause and same
// fix philosophy as SHOPIFY_TRIGGER_ORDER above: workflow.crud.js's getTriggers() groups
// triggers into category order by first-seen order while iterating findAllTriggers()'s
// unsorted result, so it's not guaranteed to stay "Message" before "Contact" and can visibly
// flip after any insert/delete on the Trigger collection (e.g. the one-off createSendTemplateTrigger.js
// script). Fixed client-side rather than adding a DB-level sort, for the same reason: this only
// changes render order, it can't affect anything else that reads trigger data.
const CATEGORY_DISPLAY_ORDER = ['Message', 'Contact', 'Shopify'];

const SHOPIFY_TRIGGER_ORDER = [
  'Order created',
  'Order cancelled',
  'Order paid',
  'Order fulfilled',
  'Fulfillment created',
  'Fulfillment updated',
  'Customer created',
  'Checkout created',
  'Abandon cart',
  'Refund created',
  'Return approved',
  'Return cancelled',
  'Return closed',
  'Return requested',
  'Out for delivery',
  'Delivered',
];

const ChooseTriggerDrawer = ({ open, onClose, editTrigger, branchId = 'start' }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const { triggers: triggersRaw, loading, error, selectedWorkflow } = useSelector(state => state.workflow);
  const triggers = Array.isArray(triggersRaw) ? triggersRaw : [];
  // Phase 6 ("Client UI") QR gating for the "Send Template" root trigger — same resolution
  // chain ChooseActionDrawer.jsx uses for its own visibleActions filter, so editing a workflow
  // while switched to a different workspace still resolves the workflow's own connection type.
  const currentWorkspace = useSelector(state => state.workspace?.currentWorkspace);
  const workspaceList = useSelector(state => state.workspace?.workspaces);
  const authWorkspaceId = useSelector(state => state.auth?.user?.workspaceId);
  const triggerWorkspaceId =
    (selectedWorkflow?.workspaceId ? String(selectedWorkflow.workspaceId) : null) ||
    currentWorkspace?._id ||
    selectedWorkflow?.workspace?._id ||
    authWorkspaceId ||
    (Array.isArray(workspaceList) ? workspaceList[0]?._id : null) ||
    null;
  const triggerWorkspaceRecord = Array.isArray(workspaceList)
    ? workspaceList.find(w => String(w._id) === String(triggerWorkspaceId))
    : null;
  // Decided by the workflow's own channel (default "whatsapp"): a Meta workflow keeps every trigger even in a QR-only workspace.
  const triggerWorkflowChannel = selectedWorkflow?.channel || null;
  const isQrConnectedWorkspace = triggerWorkflowChannel
    ? triggerWorkflowChannel === 'qr'
    : (triggerWorkspaceRecord || currentWorkspace)?.whatsappConnectionType === 'baileys';
  const [expanded, setExpanded] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [saving, setSaving] = useState(false);
  /** Main drawer scroll — reset when switching triggers or open state (matches action drawer UX). */
  const drawerBodyScrollRef = useRef(null);

  const handleAccordionChange = (category) => (event, isExpanded) => {
    setExpanded(isExpanded ? category : false);
  };

  useEffect(() => {
    if (open && triggers.length === 0 && !loading && !error) {
      dispatch(fetchTriggers());
    }
  }, [open, triggers.length, loading, error, dispatch]);

  useEffect(() => {
    if (open && editTrigger) {
      setSelectedTrigger(editTrigger);
      // Pre-fill formValues with selectedValue if present
      const newFormValues = {};
      (editTrigger.config || []).forEach(cfg => {
        (cfg.fields || []).forEach(field => {
          if (field.selectedValue !== undefined) {
            newFormValues[field.label] = field.selectedValue;
          } else if (field.type === 'select' && Array.isArray(field.options) && field.options.length > 0) {
            newFormValues[field.label] = field.options[0];
          } else {
            newFormValues[field.label] = '';
          }
        });
      });
      setFormValues(newFormValues);
    } else if (selectedTrigger) {
      const newFormValues = {};
      (selectedTrigger.config || []).forEach(cfg => {
        (cfg.fields || []).forEach(field => {
          if (field.type === 'select' && Array.isArray(field.options) && field.options.length > 0) {
            newFormValues[field.label] = field.options[0]; // default to first option
          } else {
            newFormValues[field.label] = '';
          }
        });
      });
      setFormValues(newFormValues);
    }
  }, [open, editTrigger, selectedTrigger]);

  useEffect(() => {
    if (!open) return;
    const el = drawerBodyScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [open, selectedTrigger, editTrigger]);

  // Save only dispatches and triggers the effect
  const handleSaveTrigger = async () => {
    if (!selectedTrigger) return;
    const { blocked, message: blockMsg } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 }); return; }
    
    console.log('=== Starting handleSaveTrigger ===');
    console.log('Selected trigger:', selectedTrigger.name);
    console.log('Edit trigger:', editTrigger);
    console.log('Branch ID:', branchId);
    console.log('Form values:', formValues);
    
    // Deep copy the config and add selectedValue to each field
    const configWithSelectedValues = (selectedTrigger.config || []).map(cfg => ({
      ...cfg,
      fields: (cfg.fields || []).map(field => {
        const value = formValues[field.label];
        let selectedValue;
        
        // Convert to number for number fields, especially for "no reply" trigger
        if (field.type === 'number') {
          selectedValue = value !== undefined && value !== '' ? parseInt(value) || 0 : 0;
        } else if (selectedTrigger.name === 'no reply' && ['days', 'hours', 'minutes', 'seconds'].includes(field.label)) {
          // Special handling for "no reply" trigger time fields
          selectedValue = value !== undefined && value !== '' ? parseInt(value) || 0 : 0;
        } else {
          selectedValue = value !== undefined ? value : '';
        }
        
        return {
          ...field,
          selectedValue: selectedValue,
        };
      })
    }));
    
    const triggerWithConfig = {
      ...selectedTrigger,
      config: configWithSelectedValues,
    };
    
    if (isConsoleVerbose) {
    console.log('Trigger with config:', JSON.stringify(triggerWithConfig, null, 2));
    }
    
    setSaving(true);
    
    if (editTrigger && editTrigger.branchId && editTrigger.nodeId !== undefined && editTrigger.nodeId !== null) {
      console.log('=== Updating existing trigger node ===');
      console.log('Branch:', editTrigger.branchId, 'Node ID:', editTrigger.nodeId);
      
      await dispatch(updateNodeInBranch({ 
        branchId: editTrigger.branchId, 
        nodeId: editTrigger.nodeId, 
        updates: { trigger: triggerWithConfig } 
      }));
    } else if (branchId) {
      console.log('=== Adding new trigger node ===');
      console.log('Branch:', branchId);
      
      const node = { 
        id: Date.now().toString(), 
        type: 'trigger', 
        trigger: triggerWithConfig 
      };
      await dispatch(addNodeToBranch({ branchId, node, nodeIndex: 0 })); // Triggers always at index 0
    } else {
      console.log('=== No valid branch context, aborting ===');
      setSaving(false);
      return;
    }

    // Get final state and save to backend
    const finalState = window.store?.getState ? window.store.getState() : null;
    const finalWorkflow = finalState?.workflow?.selectedWorkflow;
    
    console.log('=== Final workflow state ===');
    if (isConsoleVerbose) {
    console.log('Branches:', Object.keys(finalWorkflow?.currentVersion?.branches || {}));
    }
    if (isConsoleVerbose) {
    console.log('Full branches object:', JSON.stringify(finalWorkflow?.currentVersion?.branches, null, 2));
    }
    
    if (finalWorkflow && finalWorkflow._id) {
      // Get token from memory (not localStorage)
      const token = await getAuthToken();
      if (!token) {
        console.error("No token found");
        return;
      }
      
      try {
        console.log('=== Sending to backend ===');
        await axios.put(
          `${SERVER_DOMAIN}/api/workflow/update/${finalWorkflow._id}`,
          finalWorkflow,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log('=== Backend update successful ===');
        setSaving(false);
        if (onClose) onClose();
      } catch (err) {
        console.error('=== Backend update failed ===', err);
        setSaving(false);
      }
    } else {
      console.log('=== No workflow ID found ===');
      setSaving(false);
    }
  };

  // Reshapes an already-saved "Send Template" trigger node (see triggerWithConfig in
  // handleSendTemplateSave below for the on-disk shape) back into the flat fields
  // SendTemplateTriggerConfig's wizard reads for pre-fill (existingTrigger prop) — without
  // this, reopening an already-configured trigger to edit it always started the wizard over
  // from a blank timezone/interval/template selection.
  const buildExistingSendTemplateTrigger = (trigger) => {
    const timezoneCfg = trigger.config?.find(c => c.name === 'Select Timezone');
    const intervalCfg = trigger.config?.find(c => c.name === 'Select Time Interval');
    const getField = (cfg, label) => cfg?.fields?.find(f => f.label === label)?.selectedValue;
    return {
      timezone: getField(timezoneCfg, 'Timezone') || getField(intervalCfg, 'Timezone') || '',
      intervalValue: getField(intervalCfg, 'Select Interval Duration') ?? '',
      intervalType: getField(intervalCfg, 'Select Duration Type') || 'Minutes',
      templateName: trigger.templateName || '',
      templateId: trigger.templateId || '',
      language: trigger.language || '',
      templateCategory: trigger.templateCategory || '',
      variableMapping: Array.isArray(trigger.variableMapping) ? trigger.variableMapping : [],
      // The trigger node itself only stores `branches` (button text -> branch id); the button
      // order/text list is reconstructed from its keys, same as handleSendTemplateSave does
      // when reusing branch ids for buttons that still exist on re-save.
      quickReplyButtons: Object.keys(trigger.branches || {}).map((text) => ({ text })),
    };
  };

  // Orchestrates everything SendTemplateTriggerConfig's wizard hands back: mints/reuses one
  // branch per quick-reply button (mirroring the "Branch: Business Hours" action's pattern in
  // ChooseActionDrawer.jsx — fixed `${branchId}-trigger` seed node id, update-then-fallback-to-
  // insert, populated seed config so the existing generic button-title matching in
  // handleButtonReplyWorkflow picks it up with no backend changes), builds the trigger node
  // (config shape matches "Contact places an order" exactly so workflow.expiry.js's existing
  // generic expiry handling applies unchanged), then saves via the same
  // addNodeToBranch/updateNodeInBranch + PUT /workflow/update flow every other trigger uses.
  const handleSendTemplateSave = async (wizardResult) => {
    const {
      timezone, intervalValue, intervalType,
      templateName, templateId, language, templateCategory,
      variableMapping, quickReplyButtons,
    } = wizardResult;

    setSaving(true);
    try {
      const branchesMap = {};
      for (const button of quickReplyButtons) {
        const slug = button.text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'option';
        const buttonBranchId = editTrigger?.branches?.[button.text] || `button-${slug}-${Date.now()}`;
        branchesMap[button.text] = buttonBranchId;

        await dispatch(addBranch({ branchId: buttonBranchId }));
        const seedNode = {
          id: `${buttonBranchId}-trigger`,
          type: 'trigger',
          trigger: {
            name: 'Quick Reply Button',
            description: `Runs when the contact taps "${button.text}".`,
            triggerType: 'non-root',
            category: 'Nil',
            config: [{ name: 'Button', fields: [{ label: 'Button Title', type: 'text', selectedValue: button.text }] }],
          },
        };
        await dispatch(updateNodeInBranch({ branchId: buttonBranchId, nodeId: `${buttonBranchId}-trigger`, updates: seedNode }));
        const stateAfterUpdate = window.store?.getState ? window.store.getState() : null;
        const branchAfterUpdate = stateAfterUpdate?.workflow?.selectedWorkflow?.currentVersion?.branches?.[buttonBranchId];
        if (!branchAfterUpdate || branchAfterUpdate.length === 0) {
          await dispatch(addNodeToBranch({ branchId: buttonBranchId, node: seedNode }));
        }
      }

      const nextBranchIds = quickReplyButtons.map((b) => branchesMap[b.text]);

      const triggerWithConfig = {
        name: 'Send Template',
        description: selectedTrigger?.description || '',
        triggerType: 'root',
        category: 'Message',
        config: [
          { name: 'Select Timezone', fields: [{ label: 'Timezone', type: 'select', options: ['timezones'], selectedValue: timezone }] },
          { name: 'Select Time Interval', fields: [
              { label: 'Select Interval Duration', type: 'number', selectedValue: intervalValue },
              { label: 'Select Duration Type', type: 'select', options: ['Minutes', 'Hours', 'Days'], selectedValue: intervalType },
              { label: 'Timezone', type: 'select', options: ['timezones'], selectedValue: timezone },
          ]},
        ],
        templateName,
        templateId,
        language,
        templateCategory,
        variableMapping,
        ...(nextBranchIds.length > 0 ? { next: nextBranchIds, branches: branchesMap } : {}),
      };

      let triggerNodeId;
      let triggerBranchId;
      if (editTrigger && editTrigger.branchId && editTrigger.nodeId !== undefined && editTrigger.nodeId !== null) {
        triggerNodeId = editTrigger.nodeId;
        triggerBranchId = editTrigger.branchId;
        await dispatch(updateNodeInBranch({
          branchId: editTrigger.branchId,
          nodeId: editTrigger.nodeId,
          updates: { trigger: triggerWithConfig, next: triggerWithConfig.next },
        }));
      } else if (branchId) {
        triggerNodeId = Date.now().toString();
        triggerBranchId = branchId;
        const node = { id: triggerNodeId, type: 'trigger', trigger: triggerWithConfig, next: triggerWithConfig.next };
        await dispatch(addNodeToBranch({ branchId, node, nodeIndex: 0 }));
      } else {
        setSaving(false);
        return;
      }

      if (triggerWithConfig.next) {
        await dispatch(setNodeNextBranches({ branchId: triggerBranchId, nodeId: triggerNodeId, next: triggerWithConfig.next }));
      }

      const finalState = window.store?.getState ? window.store.getState() : null;
      const finalWorkflow = finalState?.workflow?.selectedWorkflow;
      if (finalWorkflow && finalWorkflow._id) {
        const token = await getAuthToken();
        if (token) {
          await axios.put(
            `${SERVER_DOMAIN}/api/workflow/update/${finalWorkflow._id}`,
            finalWorkflow,
            { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
          );
          setSaving(false);
          if (onClose) onClose();
          return;
        }
        console.error('No authentication token found');
      } else {
        console.error('No workflow ID found in state');
      }
      setSaving(false);
    } catch (error) {
      console.error('Error saving Send Template trigger:', error);
      setSaving(false);
      alert('Failed to save configuration. Please try again.');
    }
  };

  if (!open) return null;
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        height: '100%',
        width: 360,
        bgcolor: isDark ? '#23272b' : '#fff',
        boxShadow: 6,
        zIndex: 30,
        borderLeft: `1.5px solid ${isDark ? '#444' : '#e0e0e0'}`,
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        color: isDark ? theme.palette.text.primary : 'inherit',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {selectedTrigger && (
            <IconButton onClick={() => setSelectedTrigger(null)} sx={{ mr: 1, color: isDark ? '#fff' : '#222' }}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          )}
          <FlashOnIcon sx={{ color: 'primary.main', fontSize: 28, mr: 1 }} />
        <Typography variant="h6" fontWeight={700}>Choose a trigger</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: isDark ? theme.palette.text.primary : undefined }}><CloseIcon /></IconButton>
      </Box>
      <Box ref={drawerBodyScrollRef} sx={{
        mt: 2,
        color: isDark ? theme.palette.text.secondary : 'text.secondary',
        flex: 1,
        overflowY: 'auto',
        pb: selectedTrigger ? 8 : 0,
        p: 1,
        scrollbarWidth: { xs: 'none', sm: 'auto' },
        msOverflowStyle: { xs: 'none', sm: 'auto' },
        '&::-webkit-scrollbar': {
          width: { xs: 0, sm: 8 },
          height: { xs: 0, sm: 8 },
        },
      }}>
        {selectedTrigger ? (
          <Box sx={{ position: 'relative', minHeight: 300, pb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, color: isDark ? '#fff' : '#222' }}>{selectedTrigger.name}</Typography>
            {selectedTrigger.name === 'Teammate closes a conversation' && (
              <Box sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: isDark ? '#3d2e00' : '#fff8e1',
                border: `1px solid ${isDark ? '#7a5c00' : '#ffe082'}`,
                color: isDark ? '#ffd54f' : '#7a5c00',
              }}>
                <Typography fontSize={13} fontWeight={500}>
                  ⚠️ This trigger will not run when the contact is outside the 24-hour messaging window. Messages cannot be sent after the window expires.
                </Typography>
              </Box>
            )}
            {/* Special handling for Office-Time Flow trigger */}
            {selectedTrigger.name === 'Office-Time Flow' ? (
              <Box
                sx={{
                  width: '100%',
                  maxHeight: { xs: 'none', sm: 'none', md: 'calc(100vh - 250px)' },
                  overflowY: { xs: 'visible', sm: 'visible', md: 'auto' },
                  pb: 2,
                }}
              >
                <OfficeTimeFlowConfig 
                  workflowId={selectedWorkflow?._id} 
                  onSave={async (config) => {
                    try {
                      console.log('Office-Time Flow onSave called with config:', config);
                      
                      // Save the config to the trigger
                      const triggerWithConfig = {
                        ...selectedTrigger,
                        config: [config],
                      };
                      
                      setSaving(true);
                      
                      if (editTrigger && editTrigger.branchId && editTrigger.nodeId !== undefined && editTrigger.nodeId !== null) {
                        console.log('Updating existing trigger node');
                        await dispatch(updateNodeInBranch({ 
                          branchId: editTrigger.branchId, 
                          nodeId: editTrigger.nodeId, 
                          updates: { trigger: triggerWithConfig } 
                        }));
                      } else if (branchId) {
                        console.log('Adding new trigger node to branch:', branchId);
                        const node = { 
                          id: Date.now().toString(), 
                          type: 'trigger', 
                          trigger: triggerWithConfig 
                        };
                        await dispatch(addNodeToBranch({ branchId, node, nodeIndex: 0 }));
                      } else {
                        console.error('No branchId or editTrigger context available');
                        setSaving(false);
                        return;
                      }
                      
                      // Wait a bit for Redux state to update
                      await new Promise(resolve => setTimeout(resolve, 500));
                      
                      // Save to backend
                      const finalState = window.store?.getState ? window.store.getState() : null;
                      const finalWorkflow = finalState?.workflow?.selectedWorkflow;
                      
                      console.log('Final workflow state:', finalWorkflow);
                      
                      if (finalWorkflow && finalWorkflow._id) {
                        const token = await getAuthToken();
                        if (token) {
                          try {
                            console.log('Saving workflow to backend:', finalWorkflow._id);
                            await axios.put(
                              `${SERVER_DOMAIN}/api/workflow/update/${finalWorkflow._id}`,
                              finalWorkflow,
                              {
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${token}`,
                                },
                              }
                            );
                            console.log('Workflow saved successfully');
                            setSaving(false);
                            if (onClose) onClose();
                          } catch (err) {
                            console.error('Error saving Office-Time Flow to backend:', err);
                            console.error('Error response:', err.response?.data);
                            setSaving(false);
                            alert('Failed to save workflow. Please try again.');
                          }
                        } else {
                          console.error('No authentication token found');
                          setSaving(false);
                        }
                      } else {
                        console.error('No workflow ID found in state');
                        setSaving(false);
                      }
                    } catch (error) {
                      console.error('Error in onSave callback:', error);
                      setSaving(false);
                      alert('Failed to save configuration. Please try again.');
                    }
                  }}
                />
              </Box>
            ) : selectedTrigger.name === 'Send Template' ? (
              <Box
                sx={{
                  width: '100%',
                  maxHeight: { xs: 'none', sm: 'none', md: 'calc(100vh - 250px)' },
                  overflowY: { xs: 'visible', sm: 'visible', md: 'auto' },
                  pb: 2,
                }}
              >
                <SendTemplateTriggerConfig
                  workflowId={selectedWorkflow?._id}
                  saving={saving}
                  existingTrigger={
                    editTrigger && editTrigger.name === 'Send Template'
                      ? buildExistingSendTemplateTrigger(editTrigger)
                      : null
                  }
                  onSave={handleSendTemplateSave}
                  isQrConnectedWorkspace={isQrConnectedWorkspace}
                />
              </Box>
            ) : Array.isArray(selectedTrigger.config) && selectedTrigger.config.length > 0 ? (
              selectedTrigger.config.map((cfg, idx) => (
                <Box key={idx} sx={{ mb: 3 }}>
                  <Typography sx={{ fontWeight: 600, mb: 1, color: isDark ? '#fff' : '#222' }}>{cfg.name}</Typography>
                  {Array.isArray(cfg.fields) && cfg.fields.length > 0 ? (
                    cfg.fields.map((field, fidx) => (
                      <Box key={fidx} sx={{ mb: 2 }}>
                        <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 14 }}>{field.label}</label>
                        {field.options && Array.isArray(field.options) && field.options[0] === 'timezones' ? (
                          <Box sx={{ mt: 1, mb: 2, width: '90%' }}>
                            <ReactTimezoneSelect
                              value={formValues[field.label] || ''}
                              onChange={tz => setFormValues(v => ({ ...v, [field.label]: tz }))}
                              styles={buildTimezoneSelectStyles(isDark, {
                                controlAndMenuWidth: '90%',
                              })}
                            />
                          </Box>
                        ) : field.type === 'select' && Array.isArray(field.options) && field.options.length > 0 ? (
                          <FormControl size="small" sx={{ mt: 0.5, mb: 1, width: '90%' }}>
                            <Select
                              value={formValues[field.label] ?? ''}
                              onChange={(e) => setFormValues((v) => ({ ...v, [field.label]: e.target.value }))}
                              displayEmpty
                              inputProps={{ 'aria-label': field.label }}
                              MenuProps={{
                                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                transformOrigin: { vertical: 'top', horizontal: 'left' },
                                PaperProps: {
                                  sx: {
                                    mt: 0.5,
                                    maxHeight: 320,
                                    bgcolor: isDark ? '#2e3440' : '#fff',
                                    border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                                    '& .MuiMenuItem-root': { fontSize: 15 },
                                    '& .MuiMenuItem-root.Mui-selected': {
                                      bgcolor: isDark ? 'rgba(25, 118, 210, 0.28)' : 'action.selected',
                                    },
                                  },
                                },
                              }}
                              sx={{
                                borderRadius: '6px',
                                fontSize: 15,
                                bgcolor: isDark ? '#23272b' : '#fff',
                                color: isDark ? '#fff' : '#222',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: isDark ? '#444' : '#ccc',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: isDark ? '#666' : '#999',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderWidth: 1,
                                  borderColor: theme.palette.primary.main,
                                },
                                '& .MuiSelect-icon': {
                                  color: isDark ? '#fff' : 'inherit',
                                },
                              }}
                            >
                              {field.options.map((opt, i) => (
                                <MenuItem key={i} value={opt}>
                                  {opt}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : field.type === 'number' ? (
                          <input
                            type="number"
                            value={formValues[field.label] || ''}
                            onChange={e => setFormValues(v => ({ ...v, [field.label]: e.target.value }))}
                            style={{
                              width: '90%',
                              marginTop: 4,
                              padding: '8px 10px',
                              borderRadius: 6,
                              border: `1px solid ${isDark ? '#444' : '#ccc'}`,
                              background: isDark ? '#23272b' : '#fff',
                              color: isDark ? '#fff' : '#222',
                              fontSize: 15,
                              marginBottom: 8,
                            }}
                          />
                        ) : (
                          <input
                            type="text"
                            value={formValues[field.label] || ''}
                            onChange={e => setFormValues(v => ({ ...v, [field.label]: e.target.value }))}
                            style={{
                              width: '90%',
                              marginTop: 4,
                              padding: '8px 10px',
                              borderRadius: 6,
                              border: `1px solid ${isDark ? '#444' : '#ccc'}`,
                              background: isDark ? '#23272b' : '#fff',
                              color: isDark ? '#fff' : '#222',
                              fontSize: 15,
                              marginBottom: 8,
                            }}
                          />
                        )}
                      </Box>
                    ))
                  ) : null}
                </Box>
              ))
            ) : null}
            {selectedTrigger.name !== 'Send Template' && (
              <Box sx={{
                position: 'relative',
                bottom: 0,
                right: 0,
                width: '100%',
                zIndex: 40,
                px: 2,
                background: isDark ? '#23272b' : '#fff',
                borderTop: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                boxShadow: 0,
                display: 'flex',
                justifyContent: 'flex-start',
                py: 2,
                mt: 3,
              }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    borderRadius: '20px',
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: 'white',
                    textTransform: 'capitalize',
                    padding: '8px 16px',
                    fontWeight: 600,
                    fontSize: 16,
                    boxShadow: 2,
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.primary.dark,
                    },
                  }}
                  onClick={handleSaveTrigger}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            )}
          </Box>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={28} /></Box>
        ) : error ? (
          <Typography color="error">{error.message || error}</Typography>
        ) : triggers.length === 0 ? (
          <Typography>No triggers found.</Typography>
        ) : (
          // fetchTriggers() returns every category unfiltered (server-side, by
          // design — see workflowRepository.findAllTriggers). Scoping to the
          // workflow's own channel here keeps this purely additive: non-Shopify
          // workflows never see the new "Shopify" category (same as before it
          // existed), and Shopify workflows see only their own triggers,
          // matching the dedicated Shopify tab's intent.
          triggers.filter(categoryObj =>
            selectedWorkflow?.channel === 'shopify'
              ? categoryObj.category === 'Shopify'
              : categoryObj.category !== 'Shopify'
          ).slice().sort((a, b) => {
            const ai = CATEGORY_DISPLAY_ORDER.indexOf(a.category);
            const bi = CATEGORY_DISPLAY_ORDER.indexOf(b.category);
            return (ai === -1 ? CATEGORY_DISPLAY_ORDER.length : ai) - (bi === -1 ? CATEGORY_DISPLAY_ORDER.length : bi);
          }).map(categoryObj => {
            // Filter out non-root triggers. "Send Template" is also fully hidden here (not
            // greyed out), not just excluded from action types, for a QR-connected workspace
            // (2026-09-28 follow-up, explicit user decision) -- it's a scheduled template-blast
            // mechanism, 100% Meta-template-gated, same as every other template-sending surface.
            // "Contact places an order" is hidden the same way: it fires from Meta's order webhook, which a QR number never gets.
            let rootTriggers = categoryObj.triggers.filter(trigger =>
              (trigger.triggerType === 'root' ||
              trigger.triggerType === 'Root' ||
              trigger.triggerType === 'ROOT') &&
              !(Array.isArray(trigger.unsupportedChannels)
                ? trigger.unsupportedChannels.includes(triggerWorkflowChannel || (isQrConnectedWorkspace ? 'qr' : 'whatsapp'))
                : isQrConnectedWorkspace && (trigger.name === 'Send Template' || trigger.name === 'Contact places an order'))
            );
            if (categoryObj.category === 'Shopify') {
              rootTriggers = [...rootTriggers].sort(
                (a, b) => SHOPIFY_TRIGGER_ORDER.indexOf(a.name) - SHOPIFY_TRIGGER_ORDER.indexOf(b.name)
              );
            }

            // Only render accordion if there are root triggers in this category
            if (rootTriggers.length === 0) {
              return null;
            }
            
            return (
              <Accordion
                key={categoryObj.category}
                expanded={expanded === categoryObj.category}
                onChange={handleAccordionChange(categoryObj.category)}
                sx={{
                  bgcolor: isDark ? '#23272b' : '#f5f5f5',
                  color: isDark ? theme.palette.text.primary : theme.palette.text.primary,
                  mb: 1,
                  boxShadow: 0,
                  border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {CATEGORY_ICONS[categoryObj.category] || <CategoryIcon sx={{ mr: 1, color: 'primary.main' }} />}
                    <Box>
                      <Typography fontWeight={600}>{categoryObj.category}</Typography>
                      {CATEGORY_DESCRIPTIONS[categoryObj.category] && (
                        <Typography fontSize={12.5} color={isDark ? theme.palette.text.secondary : theme.palette.text.secondary}>
                          {CATEGORY_DESCRIPTIONS[categoryObj.category]}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0 }}>
                  {rootTriggers.map(trigger => {
                    return (
                      <Paper
                        key={trigger._id || trigger.id || trigger.name}
                        elevation={isDark ? 2 : 1}
                        sx={{
                          mb: 2,
                          p: 2,
                          bgcolor: isDark ? '#23272b' : '#fff',
                          border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                          transition: 'box-shadow 0.2s, border-color 0.2s',
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: 4,
                            borderColor: theme.palette.primary.main,
                            background: isDark ? '#2e3440' : '#f0f7ff',
                          },
                        }}
                        onClick={() => setSelectedTrigger(trigger)}
                      >
                        <Typography fontWeight={700} fontSize={16} gutterBottom>
                          {trigger.name}
                        </Typography>
                        <Typography fontSize={14} color={isDark ? theme.palette.text.secondary : theme.palette.text.secondary}>
                          {trigger.description || 'No description'}
                        </Typography>
                      </Paper>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          }).filter(Boolean) // Remove null entries
        )}
      </Box>
    </Box>
  );
};

export default ChooseTriggerDrawer; 